import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAnalysis, getGetAnalysisQueryKey } from "Infrastructure/ApiZfp/AnalyzerApi";

const POLLING_INTERVAL_MS = 15_000;

/**
 * Keeps the analysis header fresh, so an edit access handed over or taken back by another advisor shows up without
 * a reload. The poll is a query of its own that only writes successful responses into the `useGetAnalysis` cache -
 * polling that query directly would switch it to the error state on a failed poll (hiding the header) and show
 * an error toast on every interval.
 */
export const useAnalysisHeaderPolling = (analysisId: string, versionId: string, enabled: boolean) => {
  const queryClient = useQueryClient();

  useQuery({
    queryKey: ["analysisHeaderPolling", analysisId, versionId],
    queryFn: async ({ signal }) => {
      const headerQueryKey = getGetAnalysisQueryKey(analysisId, versionId);
      const startedAt = Date.now();
      const analysis = await getAnalysis(analysisId, versionId, undefined, signal);
      // a header refetch that finished meanwhile (e.g. after an own mutation) is newer than this response
      if ((queryClient.getQueryState(headerQueryKey)?.dataUpdatedAt ?? 0) < startedAt) {
        queryClient.setQueryData(headerQueryKey, analysis);
      }
      return null;
    },
    enabled: enabled && !!analysisId && !!versionId,
    // the header itself is loaded by `useGetAnalysis`, so the poll must not fire on mount
    initialData: null,
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: POLLING_INTERVAL_MS,
    retry: false,
    // Nothing reads this query's result, and React Query notifies an observer with no tracked props on every
    // state change - each poll would re-render the whole detail page (and replay every open chart's animation).
    // Only the header cache write above should cause a re-render, and only when the data really changed.
    notifyOnChangeProps: [],
    meta: { suppressErrorToast: true },
  });
};
